import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, Lock } from 'lucide-react';
import { cn } from '@client/lib/utils';

/**
 * RxButton: borderless pill, weight 600, heights 28/34/40, colour-only state changes (no lift), focus ring joins the
 * shadow stack. `primary` is the dark signature gradient (one per view); `secondary` a card-surface pill on the control
 * shadow; `ghost` is quiet; `destructive` solid red. `link` is an inline text link.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-[7px] whitespace-nowrap rounded-btn font-[600] leading-none tracking-[0.005em] transition-[color,background-color,box-shadow,filter] duration-150 ease-[var(--ease-standard)] focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-gradient-brand text-white shadow-action hover:bg-gradient-brand-hover active:brightness-95 focus-visible:[box-shadow:var(--shadow-action),0_0_0_3px_var(--ring)]',
        secondary: 'bg-card text-foreground shadow-control hover:bg-row-hover active:bg-row-active focus-visible:shadow-control-focus dark:border dark:border-border',
        ghost: 'text-muted hover:bg-row-hover hover:text-foreground active:bg-row-active',
        destructive: 'bg-[oklch(0.586_0.226_24)] text-white shadow-action hover:bg-[oklch(0.532_0.222_24)] active:bg-[oklch(0.47_0.19_24)]',
        'quiet-danger': 'text-critical hover:bg-critical-bg',
        locked: 'bg-card text-muted shadow-control cursor-not-allowed dark:border dark:border-border',
        link: 'text-link underline-offset-4 hover:underline px-0 h-auto font-[500]',
        ink: 'bg-ink text-white hover:bg-navy-deep dark:bg-white dark:text-ink dark:hover:bg-slate-200',
      },
      size: {
        md: 'h-[34px] px-[18px] text-[13px]',
        sm: 'h-7 px-3.5 text-[12.5px] [&_svg]:size-3.5',
        lg: 'h-10 px-[22px] text-[14px]',
        icon: 'size-[34px] [&_svg]:size-4',
        'icon-sm': 'size-7 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
  locked?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, loading, locked, children, disabled, type = 'button', ...props }, ref) => (
  <button ref={ref} type={type} className={cn(buttonVariants({ variant: locked ? 'locked' : variant, size }), className)} disabled={disabled || loading} aria-disabled={locked || undefined} aria-busy={loading || undefined} {...props}>
    {loading ? <Loader2 className="animate-spin" aria-hidden /> : locked ? <Lock aria-hidden /> : null}
    {children}
  </button>
));
Button.displayName = 'Button';

export { buttonVariants };
